// Color theme: follow the OS (light/dark), or a fixed light, dark or
// Darcula theme. The choice is stored per browser and applied as
// `data-theme` on <html>; index.html applies it again before first paint.

import { useEffect, useState } from 'react'

export type ThemeChoice = 'system' | 'light' | 'dark' | 'darcula'

export const THEME_OPTIONS: { value: ThemeChoice; label: string }[] = [
  { value: 'system', label: 'Match system' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'darcula', label: 'Darcula' },
]

const STORAGE_KEY = 'goblic.theme'

function readTheme(): ThemeChoice {
  try {
    const value = localStorage.getItem(STORAGE_KEY)
    return value === 'light' || value === 'dark' || value === 'darcula'
      ? value
      : 'system'
  } catch {
    return 'system'
  }
}

function applyTheme(theme: ThemeChoice): void {
  const root = document.documentElement
  if (theme === 'system') delete root.dataset.theme
  else root.dataset.theme = theme
}

export function useTheme(): [ThemeChoice, (theme: ThemeChoice) => void] {
  const [theme, setTheme] = useState<ThemeChoice>(readTheme)

  useEffect(() => {
    applyTheme(theme)
    try {
      if (theme === 'system') localStorage.removeItem(STORAGE_KEY)
      else localStorage.setItem(STORAGE_KEY, theme)
    } catch {
      // Storage unavailable (private mode): the theme still applies for this visit.
    }
  }, [theme])

  return [theme, setTheme]
}

/** Reads a color token (e.g. `term`) as a CSS color, for canvas-drawn UI like xterm. */
export function tokenColor(name: string): string {
  const channels = getComputedStyle(document.documentElement)
    .getPropertyValue(`--${name}`)
    .trim()
  return channels ? `rgb(${channels.split(/\s+/).join(', ')})` : ''
}
